import * as queryString from 'query-string';

import CookiesService from './CookiesService';
import UtilsService from './UtilsService';
import AnalyticsService, { AnalyticsEvents } from './AnalyticsService';
import type { GeoData } from './GeolocationService';
import StorageService from './StorageService';
import ABTestToolServiceV2 from './ABTestToolService.v2';
import { trackError } from '../utils/error.utils';
import type { TSignupDataTests } from '../ab-tests/types/ab-test-types';

interface UserInitiatedData {
    vendor_email?: string;
    vendor_name?: string;
    is_sso: boolean;
    sso_type?: string;
    auth_token?: string;
    auth_code?: string;
    profile_img_url?: string;
    registration_variant: 'paywall' | 'homepage' | 'homepage_facebook';
    isFromSingleCta?: boolean;
    start_trial_variant?: string;
    couponCode?: string;
    dataForIntakeFormTest?: Record<string, any>;
}

interface InitialRegistrationData {
    is_demo: string | string[] | null;
    signup_landing_page: string;
    geo_data?: GeoData;
    ab_tests: TSignupDataTests;
    referrer: string;
    signupRedirectUrl?: string;
}

export interface AdsData {
    utmCampaign: string;
    utmContent: string;
    utmMedium: string;
    utmSource: string;
    utmTerm: string;
    utmMbsy: string;
    utmOrigin: string;
    utmInitiative: string;
    liFatId: string;
    referralSource: string;
    gclid: string;
    fbclid: string;
    irclickid: string;
    placement: string;
    promo: string;
}

interface IUserDataForIntakeForm {
    name: string;
    email: string;
    auth_token: string;
    sso_type: 'google_one_tap';
    auth_code: string;
    is_sso: boolean;
    is_firebase: boolean;
    profile_img_url: string;
}

export type RegistrationData = UserInitiatedData &
    InitialRegistrationData &
    AdsData;

export interface DataForRegister {
    vendor_email?: string;
    vendor_name?: string;
    registration_variant?: 'paywall' | 'homepage' | 'homepage_facebook';
    signup_landing_page?: string;
    is_demo?: string | string[] | null;
    country_name?: string;
    city?: string;
    country_code?: string;
    longitude?: string;
    latitude?: string;
    is_sso?: boolean;
    auth_token?: string;
    auth_code?: string;
    profile_img_url?: string;
    sso_type?: string;

    utm_campaign?: string;
    utm_content?: string;
    utmMbsy?: string;
    utm_medium?: string;
    utm_source?: string;
    utm_term?: string;
    utm_origin?: string;
    utm_initiative?: string;
    li_fat_id?: string;
    referralSource?: string;
    gclid?: string;
    fbclid?: string;
    irclickid?: string;
    promo?: string;
    placement?: string;
    referrer?: string;
    ab_tests?: TSignupDataTests;
    isFromSingleCta?: boolean;
    start_trial_variant?: string;
    dataForIntakeFormTest?: Record<string, any>;
    userData?: IUserDataForIntakeForm;
}

export interface OrganizeDataForRegisterArg {
    vendor_email?: string;
    vendor_name?: string;
    registration_variant?: 'paywall' | 'homepage' | 'homepage_facebook';
    signup_landing_page?: string;
    is_demo?: string | string[] | null;
    geo_data?: {
        city: string;
        country_name: string;
        country_code: string;
        longitude: string;
        latitude: string;
    };
    is_sso: boolean;
    auth_token?: string;
    auth_code?: string;
    profile_img_url?: string;
    sso_type?: string;
    utmCampaign?: string;
    utm_campaign?: string;
    utmContent?: string;
    utm_content?: string;
    utmMbsy?: string;
    utmMedium?: string;
    utm_medium?: string;
    utmSource?: string;
    utm_source?: string;
    utmTerm?: string;
    utm_term?: string;
    utmInitiative?: string;
    utm_initiative?: string;
    liFatId?: string;
    li_fat_id?: string;
    utmOrigin?: string;
    utm_origin?: string;
    referralSource: string;
    gclid: string;
    fbclid: string;
    irclickid: string;
    promo: string;
    placement: string;
    referrer: string;
    ab_tests: TSignupDataTests;
    isFromSingleCta?: boolean;
    start_trial_variant?: string;
    dataForIntakeFormTest?: Record<string, any>;
    userData?: IUserDataForIntakeForm;
}

class RegistrationService {
    geoLocationData?: GeoData;

    getRegistrationData = (): RegistrationData => {
        let userInitiatedData: UserInitiatedData = {} as UserInitiatedData;

        const userABTests = ABTestToolServiceV2.getUserFlatABTests();

        const initialRegistrationData: InitialRegistrationData = {
            is_demo: this.isDemoRegistration(),
            signup_landing_page:
                UtilsService.getSignupLandingPageFromLocationPath(),
            geo_data: this.geoLocationData,
            ab_tests: Object.values(userABTests),
            referrer:
                CookiesService.getCookie('referringDomain') || document.referrer
        };

        const adsData = this.getUtms();

        return {
            ...userInitiatedData,
            ...initialRegistrationData,
            ...adsData
        };
    };

    getUtms = (): AdsData => {
        // if the user signed up from a page that is different from the one he started with,
        // and the original one had utms, we want to pass them to the user.
        const storedUtms = this.getInitialStoredUtmsFromCookie();
        const queryStringData = queryString.parse(window.location.search);

        // We have three places to check for UTMs :/
        // Let's improve this:
        //
        // _getUtm = Check the ssoData object if we already have it
        // if not: check the current URL params
        // if not: check the UTM store
        function getUtm(utmKey: string) {
            return queryStringData[utmKey] || storedUtms[utmKey];
        }

        const otherUtms = this.getAdditionalUtms(queryStringData);

        return {
            utmCampaign: getUtm('utm_campaign'),
            utmContent: getUtm('utm_content'),
            utmMedium: getUtm('utm_medium'),
            utmSource: getUtm('utm_source'),
            utmTerm: getUtm('utm_term'),
            utmMbsy: getUtm('code'),
            utmOrigin: getUtm('utm_origin'),
            utmInitiative: getUtm('utm_initiative'),
            liFatId: getUtm('li_fat_id'),
            referralSource: getUtm('referral_source'),
            gclid: getUtm('gclid'),
            fbclid: getUtm('fbclid'),
            irclickid: getUtm('irclickid'),
            placement: getUtm('placement'),
            promo: getUtm('promo'),
            ...otherUtms
        };
    };

    getAdditionalUtms = queryStringData => {
        const standardUtmKeys = new Set([
            'utm_campaign',
            'utm_content',
            'utm_medium',
            'utm_source',
            'utm_term',
            'code',
            'utm_origin',
            'utm_initiative',
            'li_fat_id',
            'referral_source',
            'gclid',
            'fbclid',
            'irclickid',
            'placement',
            'promo'
        ]);

        const otherUtms: Record<string, string> = {};

        Object.entries(queryStringData).forEach(([key, value]) => {
            // Skip if it's a standard UTM or value is empty
            if (standardUtmKeys.has(key) || !value) return;
            otherUtms[key] = value as string;
        });

        return otherUtms;
    };

    handleRegistration = (geoData?: GeoData): RegistrationData => {
        this.geoLocationData = geoData;
        return this.getRegistrationData();
    };

    getInitialStoredUtmsFromCookie = () => {
        let storedUtmParams = {};

        try {
            const utms: string | undefined =
                CookiesService.getCookie('utm_params');

            if (utms) {
                storedUtmParams = JSON.parse(utms);
            }
        } catch (error) {
            trackError(error, {
                message: 'unable to get stored utms from cookie'
            });
        }

        return storedUtmParams;
    };

    handleSSOUserExist(user) {
        const data = {
            id: user._id,
            email: user.email,
            name: user.full_name
        };
        window.setTimeout(() => {
            AnalyticsService.identify(user._id, data);
            AnalyticsService.track(
                AnalyticsEvents.registration_validation_redirect,
                data
            );
            AnalyticsService.track(AnalyticsEvents.sso_user_exists, data);
            setTimeout(() => {
                UtilsService.redirectTo('/app/login');
            }, 100);
        }, 10);
    }

    onRegistrationSuccessWithoutFields = (
        data?: RegistrationData,
        openInNewTab?: boolean
    ): void => {
        if (data) {
            let queryParams = '';
            let redirect = '/signup';

            const organizedData = this.organizeDataForRegister(data);

            try {
                StorageService.setItem({
                    key: 'signupData',
                    data: organizedData,
                    shouldThrowError: true
                });
            } catch (error) {
                queryParams = `?${queryString.stringify(organizedData)}`;
                trackError(error, {
                    message: 'unable to store signupData'
                });
            }
            if (data.signupRedirectUrl) {
                if (openInNewTab) {
                    window.open(data.signupRedirectUrl, '_blank');
                } else {
                    window.location.assign(data.signupRedirectUrl);
                }
                return;
            }

            const customIntakePages = [
                {
                    url: '/client-relationships',
                    source: 'client_relationships'
                }
            ];

            const matchedCustomIntakePage = customIntakePages.find(
                page => page.url === window.location.pathname
            );

            if (matchedCustomIntakePage) {
                const urlSource = queryParams
                    ? `&utm_source=${matchedCustomIntakePage.source}`
                    : `?utm_source=${matchedCustomIntakePage.source}`;
                const redirectUrl = UtilsService.getRedirectUrl(redirect);
                const fullUrl = `${redirectUrl}${queryParams}${urlSource}`;
                if (openInNewTab) {
                    window.open(fullUrl, '_blank');
                } else {
                    window.location.assign(fullUrl);
                }
            } else {
                const redirectUrl = UtilsService.getRedirectUrl(redirect);
                const fullUrl = `${redirectUrl}${queryParams}`;
                if (openInNewTab) {
                    window.open(fullUrl, '_blank');
                } else {
                    window.location.assign(fullUrl);
                }
            }
        }

        AnalyticsService.track(
            '(gatsby start trail) registration success data',
            {
                method: 'onRegistrationSuccessWithoutFields',
                data: data
            }
        );

        return;
    };

    organizeDataForRegister = (
        data: OrganizeDataForRegisterArg
    ): DataForRegister => {
        const newData: DataForRegister = {};

        if (data.vendor_email) {
            newData.vendor_email = data.vendor_email;
        }

        if (data.vendor_name) {
            newData.vendor_name = data.vendor_name;
        }

        if (data.registration_variant) {
            newData.registration_variant = data.registration_variant;
        }

        if (data.signup_landing_page) {
            newData.signup_landing_page = data.signup_landing_page;
        }

        if (data.is_demo) {
            newData.is_demo = data.is_demo;
        }

        if (data.geo_data) {
            newData.city = data.geo_data.city;
            newData.country_name = data.geo_data.country_name;
            newData.country_code = data.geo_data.country_code;
            newData.longitude = data.geo_data.longitude;
            newData.latitude = data.geo_data.latitude;
        } else if (this.geoLocationData) {
            newData.city = this.geoLocationData.city;
            newData.country_name = this.geoLocationData.country_name;
            newData.country_code = this.geoLocationData.country_code;
            newData.longitude = this.geoLocationData.longitude;
            newData.latitude = this.geoLocationData.latitude;
        }

        if (data.is_sso) {
            newData.is_sso = data.is_sso;
            newData.auth_token = data.auth_token;
            newData.auth_code = data.auth_code;
            newData.profile_img_url = data.profile_img_url;
            newData.sso_type = data.sso_type;
        }

        if (data.dataForIntakeFormTest) {
            newData.dataForIntakeFormTest = data.dataForIntakeFormTest;
        }
        if (data.userData) {
            newData.userData = data.userData;
        }

        // Utms
        newData.utm_campaign = data.utmCampaign || data.utm_campaign;
        newData.utm_content = data.utmContent || data.utm_content;
        newData.utmMbsy = data.utmMbsy;
        newData.utm_medium = data.utmMedium || data.utm_medium;
        newData.utm_source = data.utmSource || data.utm_source;
        newData.utm_term = data.utmTerm || data.utm_term;
        newData.utm_origin = data.utmOrigin || data.utm_origin;
        newData.utm_initiative = data.utm_initiative || data.utmInitiative;
        newData.li_fat_id = data.liFatId || data.li_fat_id;
        newData.referralSource = data.referralSource;
        newData.gclid = data.gclid;
        newData.fbclid = data.fbclid;
        newData.irclickid = data.irclickid;
        newData.promo = data.promo;
        newData.placement = data.placement;
        newData.referrer = data.referrer;
        newData.ab_tests = data.ab_tests;
        newData.isFromSingleCta = data.isFromSingleCta;
        newData.start_trial_variant = data.start_trial_variant;

        return newData;
    };

    isDemoRegistration = (): string | string[] | null => {
        const queryStringData: queryString.ParsedQuery<string> =
            queryString.parse(window.location.search);

        return queryStringData.is_demo || queryStringData.demo;
    };
}

export default new RegistrationService();
